import React, { useState } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const S = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0A0E1A', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  card: { background: '#111827', border: '1px solid #1E3A5F', borderRadius: 12, padding: 32, width: 480, maxWidth: '90vw' },
  title: { fontSize: 22, fontWeight: 900, color: '#F8FAFC', marginBottom: 4 },
  sub: { fontSize: 13, color: '#9CA3AF', marginBottom: 24 },
  label: { fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, display: 'block' },
  input: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box', marginBottom: 16 },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, width: '100%' }),
  error: { color: '#D32F2F', fontSize: 13, marginBottom: 12 },
  success: { color: '#2E7D32', fontSize: 13, marginBottom: 12 },
  step: (active) => ({ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }),
  stepDot: (active, done) => ({ width: 28, height: 28, borderRadius: '50%', background: done ? '#2E7D32' : active ? '#1E3A5F' : '#1E3A5F22', border: `2px solid ${done ? '#2E7D32' : active ? '#1E3A5F' : '#1E3A5F44'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#F8FAFC', flexShrink: 0 }),
  divider: { height: 1, background: '#1E3A5F44', margin: '16px 0' },
  planCard: (selected) => ({ background: selected ? '#1E3A5F' : '#1E3A5F11', border: `2px solid ${selected ? '#1E3A5F' : '#1E3A5F44'}`, borderRadius: 8, padding: 16, cursor: 'pointer', marginBottom: 10 }),
};

const PLANS = [
  { id: 'starter', name: 'Starter', price: '£99/mo', officers: '10 officers', sites: '1 site', features: 'Basic reporting, incident logs' },
  { id: 'professional', name: 'Professional', price: '£249/mo', officers: '50 officers', sites: '5 sites', features: 'Full analytics, PDF export, NFC patrol' },
  { id: 'enterprise', name: 'Enterprise', price: '£599/mo', officers: 'Unlimited', sites: 'Unlimited', features: 'White label, API access, priority support' },
];

export default function TenantOnboarding({ onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [company, setCompany] = useState({ name: '', industry: '', address: '', phone: '' });
  const [admin, setAdmin] = useState({ name: '', email: '', password: '', sia: '' });
  const [plan, setPlan] = useState('professional');
  const [tenantId, setTenantId] = useState('');

  const generateTenantId = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').substring(0, 30);

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (!company.name.trim()) { setError('Company name required'); return; }
    setTenantId(generateTenantId(company.name));
    setError('');
    setStep(2);
  };

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    if (!admin.email || !admin.password) { setError('Email and password required'); return; }
    if (admin.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setStep(3);
  };

  const handlePlanSubmit = (e) => {
    e.preventDefault();
    setStep(4);
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, admin.email, admin.password);
      const uid = userCredential.user.uid;

      // Create tenant document
      await setDoc(doc(db, 'tenants', tenantId), {
        name: company.name,
        industry: company.industry,
        address: company.address,
        phone: company.phone,
        plan: plan,
        active: true,
        admin_uid: uid,
        created_at: serverTimestamp(),
        tenant_id: tenantId,
        officer_count: 0,
        site_count: 0,
      });

      // Create admin user document
      await setDoc(doc(db, 'users', uid), {
        name: admin.name,
        email: admin.email,
        sia_number: admin.sia,
        role: 'facility_manager',
        tenant_id: tenantId,
        active: true,
        created_at: serverTimestamp(),
      });

      // Create welcome notification
      await addDoc(collection(db, 'notifications'), {
        type: 'system',
        title: '🎉 Welcome to oSecure Ops',
        body: `${company.name} is now live on the ${plan} plan.`,
        tenant_id: tenantId,
        sent_at: serverTimestamp(),
        read: false,
      });

      setStep(5);
      if (onComplete) onComplete(tenantId);
    } catch (err) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Company', 'Admin', 'Plan', 'Confirm', 'Done'];

  return (
    <div style={S.overlay}>
      <div style={S.card}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src="/osecurity-logo.png" style={{ width: 40, height: 40, borderRadius: 8 }} alt="Osecurity" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, letterSpacing: 1, color: '#F8FAFC' }}>OSECURE OPS</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>Client onboarding</div>
          </div>
        </div>

        {/* Steps indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, alignItems: 'center' }}>
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div style={S.stepDot(step === i + 1, step > i + 1)}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 11, color: step === i + 1 ? '#F8FAFC' : '#9CA3AF', flex: i < steps.length - 1 ? 0 : 1 }}>{s}</div>
              {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? '#2E7D32' : '#1E3A5F44' }} />}
            </React.Fragment>
          ))}
        </div>

        {error && <div style={S.error}>{error}</div>}

        {/* Step 1 — Company */}
        {step === 1 && (
          <form onSubmit={handleCompanySubmit}>
            <div style={S.title}>Company details</div>
            <div style={S.sub}>Tell us about your organisation.</div>
            <label style={S.label}>Company name *</label>
            <input style={S.input} placeholder="e.g. Canary Wharf Group" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} required />
            <label style={S.label}>Industry</label>
            <select style={S.input} value={company.industry} onChange={e => setCompany({ ...company, industry: e.target.value })}>
              <option value="">Select industry</option>
              <option>Corporate HQ</option>
              <option>Shopping Centre</option>
              <option>Construction</option>
              <option>Healthcare</option>
              <option>Education</option>
              <option>Residential</option>
              <option>Hospitality</option>
              <option>Other</option>
            </select>
            <label style={S.label}>Address</label>
            <input style={S.input} placeholder="Head office address" value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} />
            <label style={S.label}>Phone</label>
            <input style={S.input} placeholder="+44 20 xxxx xxxx" value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} />
            <button type="submit" style={S.btn()}>Continue →</button>
          </form>
        )}

        {/* Step 2 — Admin user */}
        {step === 2 && (
          <form onSubmit={handleAdminSubmit}>
            <div style={S.title}>Facility Manager account</div>
            <div style={S.sub}>Create the primary admin account for your organisation.</div>
            <label style={S.label}>Full name *</label>
            <input style={S.input} placeholder="Your full name" value={admin.name} onChange={e => setAdmin({ ...admin, name: e.target.value })} required />
            <label style={S.label}>Work email *</label>
            <input style={S.input} type="email" placeholder="you@company.com" value={admin.email} onChange={e => setAdmin({ ...admin, email: e.target.value })} required />
            <label style={S.label}>Password *</label>
            <input style={S.input} type="password" placeholder="Min 6 characters" value={admin.password} onChange={e => setAdmin({ ...admin, password: e.target.value })} required />
            <label style={S.label}>SIA licence number (optional)</label>
            <input style={S.input} placeholder="8-digit SIA number" value={admin.sia} onChange={e => setAdmin({ ...admin, sia: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setStep(1)} style={{ ...S.btn('#1E3A5F44'), flex: 1 }}>← Back</button>
              <button type="submit" style={{ ...S.btn(), flex: 2 }}>Continue →</button>
            </div>
          </form>
        )}

        {/* Step 3 — Plan */}
        {step === 3 && (
          <form onSubmit={handlePlanSubmit}>
            <div style={S.title}>Choose your plan</div>
            <div style={S.sub}>All plans include a 14-day free trial.</div>
            {PLANS.map(p => (
              <div key={p.id} style={S.planCard(plan === p.id)} onClick={() => setPlan(p.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, color: '#F8FAFC', fontSize: 15 }}>{p.name}</div>
                  <div style={{ fontWeight: 900, color: plan === p.id ? '#C9A84C' : '#9CA3AF', fontSize: 16 }}>{p.price}</div>
                </div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>{p.officers} · {p.sites} · {p.features}</div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="button" onClick={() => setStep(2)} style={{ ...S.btn('#1E3A5F44'), flex: 1 }}>← Back</button>
              <button type="submit" style={{ ...S.btn(), flex: 2 }}>Continue →</button>
            </div>
          </form>
        )}

        {/* Step 4 — Confirm */}
        {step === 4 && (
          <div>
            <div style={S.title}>Confirm & launch</div>
            <div style={S.sub}>Review your details before going live.</div>
            <div style={{ background: '#1E3A5F11', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Organisation</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>{company.name}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>{company.industry} · {company.address}</div>
            </div>
            <div style={{ background: '#1E3A5F11', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Facility Manager</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>{admin.name}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>{admin.email}</div>
            </div>
            <div style={{ background: '#1E3A5F11', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Plan</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#C9A84C' }}>{PLANS.find(p => p.id === plan)?.name} — {PLANS.find(p => p.id === plan)?.price}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>14-day free trial · Tenant ID: {tenantId}</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(3)} style={{ ...S.btn('#1E3A5F44'), flex: 1 }}>← Back</button>
              <button onClick={handleFinalSubmit} disabled={loading} style={{ ...S.btn('#2E7D32'), flex: 2 }}>
                {loading ? 'Creating account…' : '🚀 Launch platform'}
              </button>
            </div>
          </div>
        )}

        {/* Step 5 — Done */}
        {step === 5 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={S.title}>You're live!</div>
            <div style={S.sub}>Your oSecure Ops platform is ready. Your tenant ID is:</div>
            <div style={{ background: '#1E3A5F', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 16, color: '#C9A84C', marginBottom: 24 }}>{tenantId}</div>
            <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 24 }}>
              Share this with your security officers so they can register on the guard app.
            </div>
            <button onClick={() => window.location.reload()} style={S.btn()}>Open Control Room →</button>
          </div>
        )}
      </div>
    </div>
  );
}